import StateObserver from 'src/StateObserver';
import { captureMessage } from '@sentry/browser';
import { startInstallNativeSequence } from 'src/game/native/native.sequence';
import { isWebIapAvailable } from 'src/lib/iap/iap.util';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { captureGenericError } from 'src/lib/sentry';
import * as Match2 from 'src/replicant/match2';
import {
  getMatch2Level,
  isMatch2Available,
} from 'src/replicant/match2/match2.getters';
import { joinLeague } from './leagues/league.sequences';
import {
  resolveMatch2SequencePromise,
  sessionNonSkipGamesPlayed,
  startMatch2,
} from './match2.sequence';
import { canSeeArena } from 'src/replicant/championsLeague/championsLeague.getters';
import { goToArenaFromHome } from 'src/game/championsLeague/championsLeague.sequence';
import { isSceneEntered } from 'src/lib/stateUtils';
import { rescueLevelGetActive } from 'src/replicant/rescueLevel/rescueLevel.getters';
import { playRescueLevel } from 'src/game/rescueLevel/rescueLevel.sequence';
import { dreamDriftIsPlaying } from 'src/game/dreamDrift/dreamDrift.utils';

/**
 * Play the current match2 puzzle level.
 * @param args - Optional configuration object.
 * @param args.onOk - Callback invoked right after user tap to play.
 * @param args.onStart - Callback invoked when the Match2 scene is entered.
 * @param args.coinsPinch - If true, enables the "coins pinch" feature for the session.
 * @returns A promise that resolves when the flow completes or is aborted.
 */
export async function playMatch2({
  onOk,
  onStart,
  coinsPinch,
  origin,
}: {
  onOk?: () => void;
  onStart?: () => void;
  coinsPinch?: boolean;
  origin: PopupDialogOrigin | undefined;
}) {
  // Dream Drift owns the puzzle scene until its exit transition finishes.
  if (dreamDriftIsPlaying()) {
    // Record blocked entry so unexpected no-op Play actions can be investigated.
    captureMessage('Match2 start blocked by Dream Drift', 'warning');
    resolveMatch2SequencePromise();
    return;
  }

  const { user } = StateObserver.getState();
  const now = StateObserver.now();

  // Handle edge case where rescue level is active but user selected to play
  // a level from other features like Tournaments or Fuji Quest
  if (rescueLevelGetActive(user)) {
    await playRescueLevel({ origin: origin ?? 'hud' });
  }

  // If already playing, bail.
  if (user.match2.playing) {
    captureGenericError(
      'Cannot start match2 game',
      new Error('Already playing'),
      { level: 'warning' },
    );
    return;
  }

  // If match2 is locked, show the popup explaining why, and bail.
  const minigame = getMatch2Locked();
  if (minigame.locked) {
    await openPopupPromise('popupFeatureDisabled', {
      feature: 'game',
      reason: minigame.reason,
      origin,
    });
    return;
  }

  // Start the install sequence if needed.
  if (
    !isWebIapAvailable() &&
    Match2.getters.canShowMatch2PlayOnAppPopup(
      user,
      now,
      sessionNonSkipGamesPlayed,
    )
  ) {
    await startInstallNativeSequence({ originFeature: 'match2_pregame' });
  }

  // Join league if needed.
  await joinLeague(origin);

  // Got to arena room if user is playing Champions League
  if (canSeeArena(user, now) && isSceneEntered('livingRoom')) {
    goToArenaFromHome();
  }

  // Open the match2 pregame popup.
  const result = await openPopupPromise('popupMatch2Pregame', {
    useClickOutside: true,
    level: getMatch2Level(user, true),
    coinsPinch,
    origin,
  });

  // Result can potentially be undefined if `closeAllPopups` is called.
  if (!result?.success) {
    // Resolve the match2 sequence promise to avoid blocking other sequences.
    resolveMatch2SequencePromise();
    return;
  }

  // Call the onOk callback if user tap the play button
  onOk?.();

  // Start the match2 game.
  await startMatch2({
    selectedPowerups: result.powerups,
    coinsPinch,
    onSceneEntered: onStart, // Call the onSelected callback if provided.
  });
}

/**
 * Get the locked status of the minigame.
 * @returns
 */
export function getMatch2Locked() {
  const { user } = StateObserver.getState();
  const areMinigamesAvailable = isMatch2Available(user);
  if (!areMinigamesAvailable) {
    return { locked: true };
  }
  return { locked: false, reason: undefined };
}
